import React                from 'react'
import { SnackbarActions, GlobalLoaderActions }  from 'actionsets'
import StyleSheet           from 'styles'
import { humanize }         from 'utils'
import FormComponentsMixin  from './FormComponentsMixin'
import ErrorObject          from './ErrorObject'
import { deepMerge } from 'utils'

export const FormMixin = superclass => class extends FormComponentsMixin(superclass){
  constructor(props){
    super(props)
    if(this.formObject === undefined){
      throw new Error('Classes including the FormMixin must provide a formObject property. `formObject` should be the object being edited in the current form')
    }
    this.save = this.save.bind(this)
    this.errorObject = new ErrorObject(this)
    this.styles = styles
    this.state = { attributes: {} }
    if(!this.onSaveRoute)
      this.onSaveRoute = '/'
    SnackbarActions.bindActions(this, 'snackbar')
    GlobalLoaderActions.bindActions(this, 'loader')
  }

  dependsOn(){
    if(this.objectId){
      return this.actions.show({id: this.objectId})
    }
    else {
      return this.actions.resetState()
    }
  }

  dependenciesMet(){
    return !!(this.createMode || this.formObject.id)
  }

  hasError = fieldName => {
    return this.errorObject.hasError(fieldName)
  }

  getError = fieldName => {
    return this.errorObject.getError(fieldName)
  }

  get saveActionName() {
    return this.editMode ? 'update' : 'create'
  }

  get saveAction() {
    return this.getAction(this.saveActionName)
  }

  get editMode(){
    return !this.createMode
  }

  get createMode(){
    return !!this.props.match.path.match(/\/new(?:\/[^0-9]+)?$/) && !this.formObject.id
  }

  get objectId(){
    return this.props.match.params.id
  }

  attributeChangedFromNthArgument = (attribute, nth) => (...args) => {
    this.attributeChanged(attribute)({target: { value: args[nth] }})
  }

  attribute = (name, defaultValue='') => {
    return this.deepGet(name, this.baseUpdatedFormObject, defaultValue)
  }

  get updatedFormObject(){
    return this.baseUpdatedFormObject
  }

  get baseUpdatedFormObject() {
    return deepMerge(this.formObject, this.state.attributes)
  }

  get formObjectName(){
    return this.updatedFormObject.name
  }

  onSaveSuccess(formObject){
    this.actions.loader.stopLoad()
    if(!!this.onSaveRoute){
      this.props.history.push(this.onSaveRoute)
    }
    this.actions.snackbar.success(this.saveSuccessMessage)
  }

  get saveSuccessMessage() {
    return `Saved: "${this.formObjectName}"`
  }

  onSaveFailed(formObject){
    this.actions.loader.stopLoad()
    this.actions.snackbar.error(this.saveFailMessage)
  }

  get saveFailMessage() {
    return `Error saving: ${this.formObjectName}`
  }

  get canSave() {
    return true
  }

  async save(){
    if(!this.canSave) {
      return
    }
    return this.saveObject(this.updatedFormObject)
  }

  saveObject = async (formObject, save = () => this.saveAction(formObject) ) => {
    this.actions.loader.startLoad()
    try{
      await save()
      this.onSaveSuccess(formObject)
    }catch(err){
      console.error(err)
      this.onSaveFailed(formObject)
      throw err
    }
  }

  getAction(name) {
    return this.actions[name]
  }

  renderErrorMessages = () => {
    if(this.error){
      const {title, meta} = this.error
      return (
        <div className={styles.errorBox}>
          <p>{title}</p>
          {Object.entries(meta || {}).map(([attribute, values]) =>
            <p key={attribute}>{humanize(attribute)}: {values.join(', ')}</p>
          )}
        </div>
      )
    }
  }
}

const styles = StyleSheet({
  error: {
    color: 'red'
  },
  errorBox: {
    borderLeft: '4px solid #f36464',
    padding: 10,
    background: '#fbe3e3',
    color: '#d23232',
    margin: 10,
  },
  container: {
    display: 'flex'
  },
  table: {
    marginTop: 10,
    width: '100%',
    '& input': {
      fontSize: 17,
      width: '90%',
      padding: 5
    },
    '& textarea': {
      fontSize: 17,
      width: '90%',
      padding: 5
    },
    '& select': {
      fontSize: 17,
      width: '90%',
      padding: 5
    }
  },
  col1: {
    width: 130,
    textAlign: 'right',
    marginRight: 20
  }
})

export default FormMixin